import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, FileText, Edit, Trash2, CheckCircle, Send, Download, ArrowRight, Eye, FileDown } from 'lucide-react';
import PageTour from '@/components/PageTour';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-info/20 text-info',
  sent: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-success/20 text-success',
  rejected: 'bg-error/20 text-error',
  converted: 'bg-success/20 text-success',
  expired: 'bg-orange-100 text-orange-700',
};

const emptyItem = { description: '', quantity: 1, unit_price: 0 };

export default function QuotationsPage() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_name: '', customer_id: '', items: [{ ...emptyItem }],
    tax_rate: 15, discount: 0, validity_date: '', terms_conditions: '', notes: '', source: 'accounting',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [qRes, cRes] = await Promise.all([
        api.get('/quotations'),
        api.get('/customers').catch(() => ({ data: { data: [] } })),
      ]);
      setQuotations(qRes.data);
      setCustomers(Array.isArray(cRes.data) ? cRes.data : cRes.data?.data || []);
    } catch { toast.error('Failed to load quotations'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ customer_name: '', customer_id: '', items: [{ ...emptyItem }], tax_rate: 15, discount: 0, validity_date: '', terms_conditions: '', notes: '', source: 'accounting' });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.items.some(i => i.description && i.quantity > 0)) { toast.error('Add at least one item'); return; }
    try {
      if (editing) { await api.put(`/quotations/${editing.id}`, form); toast.success('Quotation updated'); }
      else { await api.post('/quotations', form); toast.success('Quotation created'); }
      setShowForm(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (q) => {
    setEditing(q);
    setForm({ customer_name: q.customer_name, customer_id: q.customer_id || '', items: q.items || [{ ...emptyItem }], tax_rate: q.tax_rate || 0, discount: q.discount || 0, validity_date: q.validity_date || '', terms_conditions: q.terms_conditions || '', notes: q.notes || '', source: q.source || 'accounting' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try { await api.delete(`/quotations/${id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.put(`/quotations/${id}/status`, { status }); toast.success(`Status updated to ${status}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleConvert = async (id) => {
    if (!window.confirm('Convert this quotation to a sale?')) return;
    try { await api.post(`/quotations/${id}/convert-to-sale`); toast.success('Converted to sale!'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleExport = async (id, fmt) => {
    try {
      const res = await api.get(`/quotations/${id}/export/${fmt}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = fmt === 'pdf' ? `quotation_${id}.pdf` : `quotation_${id}.doc`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch { toast.error('Export failed'); }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, val) => setForm(f => ({ ...f, items: f.items.map((item, i) => i === idx ? { ...item, [field]: val } : item) }));

  const calcSubtotal = () => form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const calcTax = () => ((calcSubtotal() - (form.discount || 0)) * (form.tax_rate || 0)) / 100;
  const calcTotal = () => calcSubtotal() - (form.discount || 0) + calcTax();

  const filtered = quotations.filter(q => {
    if (filterStatus !== 'all' && q.status !== filterStatus) return false;
    if (search && !q.quotation_number?.toLowerCase().includes(search.toLowerCase()) && !q.customer_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="quotations-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 flex items-center justify-center shadow-lg">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight" data-testid="quotations-title">Quotations</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Create, manage and convert quotations to sales</p>
            </div>
          </div>
          <Button className="rounded-full" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-quotation-btn">
            <Plus size={18} className="mr-2" />New Quotation
          </Button>
        </div>
        <div className="flex gap-2">
          <PageTour storageKey="tour_quotations" steps={[
            { target: '[data-testid="add-quotation-btn-2"]', title: 'Create Quotation', content: 'Click here to create a new price quotation for a customer with items, prices, tax, and discount.' },
            { target: '[data-testid="quotation-search"]', title: 'Search & Filter', content: 'Search quotations by number or customer name. Use the status filter to see drafts, approved, or sent quotations.' },
            { target: '[data-testid="quotations-table"]', title: 'Quotation List', content: 'All quotations are listed here. Use the action buttons to view details, approve, send, export PDF/Word, or convert to a sale.' },
          ]} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['draft', 'approved', 'sent', 'accepted', 'converted'].map(s => (
            <Card key={s} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground capitalize">{s}</p>
                <p className="text-2xl font-bold mt-1">{quotations.filter(q => q.status === s).length}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input placeholder="Search quotations..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="quotation-search-2" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="quotation-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Quotations List */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quotations-table-2">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-medium">#</th>
                    <th className="p-3 text-left font-medium">Customer</th>
                    <th className="p-3 text-left font-medium">Items</th>
                    <th className="p-3 text-right font-medium">Total</th>
                    <th className="p-3 text-left font-medium">Status</th>
                    <th className="p-3 text-left font-medium">Date</th>
                    <th className="p-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(q => (
                    <tr key={q.id} className="border-b hover:bg-muted/30 transition-colors" data-testid="quotation-row">
                      <td className="p-3 font-mono text-xs">{q.quotation_number}</td>
                      <td className="p-3">{q.customer_name || '-'}</td>
                      <td className="p-3 text-muted-foreground">{q.items?.length || 0} items</td>
                      <td className="p-3 text-right font-semibold">{q.currency_code} {q.total?.toFixed(2)}</td>
                      <td className="p-3"><Badge className={STATUS_COLORS[q.status] || 'bg-gray-100'} data-testid="quotation-status">{q.status}</Badge></td>
                      <td className="p-3 text-muted-foreground text-xs">{q.created_at?.slice(0, 10)}</td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => setShowDetail(q)} data-testid="view-quotation-btn"><Eye size={14} /></Button>
                          {q.status === 'draft' && <Button size="sm" variant="ghost" onClick={() => handleEdit(q)} data-testid="edit-quotation-btn"><Edit size={14} /></Button>}
                          {q.status === 'draft' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(q.id, 'approved')} title="Approve"><CheckCircle size={14} className="text-info" /></Button>}
                          {q.status === 'approved' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(q.id, 'sent')} title="Mark Sent"><Send size={14} className="text-indigo-500" /></Button>}
                          {(q.status === 'sent' || q.status === 'accepted') && q.status !== 'converted' && <Button size="sm" variant="ghost" onClick={() => handleConvert(q.id)} title="Convert to Sale"><ArrowRight size={14} className="text-success" /></Button>}
                          <Button size="sm" variant="ghost" onClick={() => handleExport(q.id, 'pdf')} title="Export PDF"><FileDown size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(q.id)} className="text-error" data-testid="delete-quotation-btn"><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No quotations found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="quotation-form-dialog">
            <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Edit' : 'New'} Quotation</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Customer Name *</Label>
                  <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required placeholder="Customer name" data-testid="quotation-customer-input" />
                </div>
                <div>
                  <Label>Customer (from list)</Label>
                  <Select value={form.customer_id} onValueChange={v => { const c = customers.find(x => x.id === v); setForm({ ...form, customer_id: v, customer_name: c?.name || form.customer_name }); }}>
                    <SelectTrigger><SelectValue placeholder="Select existing" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.ref ? ` (${c.ref})` : ''}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tax Rate (%)</Label>
                  <Input type="number" step="0.1" min="0" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Discount</Label>
                  <Input type="number" step="0.01" min="0" value={form.discount} onChange={e => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input type="date" value={form.validity_date} onChange={e => setForm({ ...form, validity_date: e.target.value })} data-testid="quotation-validity-input" />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-base font-semibold">Items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus size={14} className="mr-1" />Add Item</Button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-5" placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} data-testid={`item-desc-${idx}`} />
                      <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 0)} />
                      <Input className="col-span-3" type="number" step="0.01" min="0" placeholder="Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                      <span className="col-span-1 text-sm font-semibold text-right">{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-error" onClick={() => removeItem(idx)}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-right space-y-1 text-sm">
                  <p>Subtotal: <span className="font-semibold">{calcSubtotal().toFixed(2)}</span></p>
                  {form.discount > 0 && <p>Discount: <span className="font-semibold text-error">-{form.discount.toFixed(2)}</span></p>}
                  <p>Tax ({form.tax_rate}%): <span className="font-semibold">{calcTax().toFixed(2)}</span></p>
                  <p className="text-base font-bold">Total: {calcTotal().toFixed(2)}</p>
                </div>
              </div>

              <div>
                <Label>Terms & Conditions</Label>
                <Textarea value={form.terms_conditions} onChange={e => setForm({ ...form, terms_conditions: e.target.value })} placeholder="Payment terms, delivery conditions..." rows={3} />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." rows={2} />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="rounded-full" data-testid="submit-quotation-btn">{editing ? 'Update' : 'Create'} Quotation</Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="quotation-detail-dialog">
            {showDetail && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-outfit flex items-center gap-3">
                    {showDetail.quotation_number}
                    <Badge className={STATUS_COLORS[showDetail.status]}>{showDetail.status}</Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{showDetail.customer_name || '-'}</span></div>
                    <div><span className="text-muted-foreground">Created:</span> <span className="font-medium">{showDetail.created_at?.slice(0, 10)}</span></div>
                    <div><span className="text-muted-foreground">Valid Until:</span> <span className="font-medium">{showDetail.validity_date || '-'}</span></div>
                    <div><span className="text-muted-foreground">Created By:</span> <span className="font-medium">{showDetail.created_by_name}</span></div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead><tr className="bg-muted/50"><th className="p-2 text-left">#</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                      <tbody>
                        {showDetail.items?.map((item, i) => (
                          <tr key={i} className="border-t"><td className="p-2">{i + 1}</td><td className="p-2">{item.description}</td><td className="p-2 text-right">{item.quantity}</td><td className="p-2 text-right">{item.unit_price?.toFixed(2)}</td><td className="p-2 text-right font-semibold">{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="text-right space-y-1 text-sm">
                    <p>Subtotal: <span className="font-semibold">{showDetail.currency_code} {showDetail.subtotal?.toFixed(2)}</span></p>
                    {showDetail.discount > 0 && <p>Discount: <span className="text-error font-semibold">-{showDetail.discount?.toFixed(2)}</span></p>}
                    <p>Tax ({showDetail.tax_rate}%): <span className="font-semibold">{showDetail.tax_amount?.toFixed(2)}</span></p>
                    <p className="text-lg font-bold">{showDetail.currency_code} {showDetail.total?.toFixed(2)}</p>
                  </div>

                  {showDetail.terms_conditions && <div className="p-3 bg-muted/50 rounded-lg"><p className="text-xs text-muted-foreground mb-1">Terms & Conditions</p><p className="text-sm">{showDetail.terms_conditions}</p></div>}
                  {showDetail.notes && <div className="p-3 bg-muted/50 rounded-lg"><p className="text-xs text-muted-foreground mb-1">Notes</p><p className="text-sm">{showDetail.notes}</p></div>}

                  <div className="flex gap-2 flex-wrap">
                    {showDetail.status === 'draft' && <Button size="sm" onClick={() => { handleStatusChange(showDetail.id, 'approved'); setShowDetail(null); }} data-testid="approve-quotation-btn"><CheckCircle size={14} className="mr-1" />Approve</Button>}
                    {showDetail.status === 'approved' && <Button size="sm" onClick={() => { handleStatusChange(showDetail.id, 'sent'); setShowDetail(null); }}><Send size={14} className="mr-1" />Mark Sent</Button>}
                    {showDetail.status === 'sent' && <Button size="sm" variant="outline" onClick={() => { handleStatusChange(showDetail.id, 'accepted'); setShowDetail(null); }}><CheckCircle size={14} className="mr-1" />Accept</Button>}
                    {showDetail.status === 'sent' && <Button size="sm" variant="destructive" onClick={() => { handleStatusChange(showDetail.id, 'rejected'); setShowDetail(null); }}>Reject</Button>}
                    {(showDetail.status === 'accepted' || showDetail.status === 'sent') && showDetail.status !== 'converted' && <Button size="sm" className="bg-success hover:bg-emerald-700" onClick={() => { handleConvert(showDetail.id); setShowDetail(null); }}><ArrowRight size={14} className="mr-1" />Convert to Sale</Button>}
                    <Button size="sm" variant="outline" onClick={() => handleExport(showDetail.id, 'pdf')} data-testid="export-pdf-btn"><Download size={14} className="mr-1" />PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => handleExport(showDetail.id, 'word')} data-testid="export-word-btn"><FileDown size={14} className="mr-1" />Word</Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
